import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClassroomAccess,
  CurriculumSubject,
  HomeworkAssignmentDetail,
  HomeworkAssignmentSummary,
  HomeworkSummary,
  LessonDetail,
  LessonSummary,
  Paginated,
  ReceivedInvitation,
  ResourceSummary,
  SentInvitation,
  StudentDashboard,
  StudentListItem,
  StudentProgressOverview,
  TutorDashboard,
} from '@tutor/shared';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-client';

/* ------------------------------ dashboards ------------------------------ */

export function useTutorDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard.tutor,
    queryFn: () => api.get<TutorDashboard>('/api/dashboard/tutor'),
    // The classroom "join" window opens on a clock, so the dashboard must not go stale.
    refetchInterval: 60_000,
  });
}

export function useStudentDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard.student,
    queryFn: () => api.get<StudentDashboard>('/api/dashboard/student'),
    refetchInterval: 60_000,
  });
}

/* -------------------------------- lessons ------------------------------- */

export type LessonFilters = {
  scope?: 'all' | 'today' | 'upcoming' | 'past';
  from?: string;
  to?: string;
  status?: string;
  studentId?: string;
  subjectId?: string;
  page?: number;
  pageSize?: number;
}

export function useLessons(filters: LessonFilters = {}) {
  return useQuery({
    queryKey: queryKeys.lessons.list(filters),
    queryFn: () => api.get<Paginated<LessonSummary>>('/api/lessons', { query: filters }),
  });
}

export function useLesson(lessonId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.lessons.detail(lessonId ?? ''),
    queryFn: () => api.get<LessonDetail>(`/api/lessons/${lessonId}`),
    enabled: Boolean(lessonId),
  });
}

/**
 * The classroom gate. A 403 here is the authoritative answer — the page renders an
 * access-denied state from it rather than trusting the route it was reached through.
 */
export function useClassroomAccess(lessonId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.lessons.classroom(lessonId ?? ''),
    queryFn: () => api.get<ClassroomAccess>(`/api/lessons/${lessonId}/classroom`),
    enabled: Boolean(lessonId),
    retry: false,
    staleTime: 0,
  });
}

export function useEndLesson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lessonId: string) => api.post<LessonSummary>(`/api/lessons/${lessonId}/end`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.tutor });
    },
  });
}

export function useAddLessonNote(lessonId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; isPrivate: boolean }) =>
      api.post(`/api/lessons/${lessonId}/notes`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.lessons.detail(lessonId) }),
  });
}

/* ------------------------------- students ------------------------------- */

export function useStudents(filters: { search?: string; subjectId?: string; page?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.students.list(filters),
    queryFn: () => api.get<Paginated<StudentListItem>>('/api/students', { query: filters }),
  });
}

export interface StudentDetailResponse {
  user: StudentListItem['user'];
  profile: StudentListItem['profile'];
  subjects: StudentListItem['subjects'];
  relationship: Array<{
    id: string;
    subject: { id: string; name: string; slug: string; colour: string | null } | null;
    status: string;
    startDate: string;
    endDate: string | null;
  }>;
  progress: StudentProgressOverview['subjects'];
  topics: StudentProgressOverview['topics'];
  assessments: Array<{
    id: string;
    title: string;
    type: string;
    score: number | null;
    maxScore: number | null;
    grade: string | null;
    subjectName: string | null;
    assessedAt: string;
    assessorName: string;
  }>;
  upcomingLessons: LessonSummary[];
  recentHomework: Array<{
    id: string;
    title: string;
    status: string;
    dueAt: string;
    submittedAt: string | null;
    score: number | null;
    maxScore: number | null;
  }>;
}

export function useStudent(studentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.students.detail(studentId ?? ''),
    queryFn: () => api.get<StudentDetailResponse>(`/api/students/${studentId}`),
    enabled: Boolean(studentId),
  });
}

/* ------------------------------- homework ------------------------------- */

export type HomeworkFilters = {
  scope?: 'all' | 'to-mark' | 'due' | 'overdue' | 'marked';
  status?: string;
  studentId?: string;
  subjectId?: string;
  page?: number;
  pageSize?: number;
}

export function useHomeworkAssignments(filters: HomeworkFilters = {}) {
  return useQuery({
    queryKey: queryKeys.homework.list(filters),
    queryFn: () => api.get<Paginated<HomeworkAssignmentSummary>>('/api/homework', { query: filters }),
  });
}

export function useHomeworkAssignment(assignmentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.homework.detail(assignmentId ?? ''),
    queryFn: () => api.get<HomeworkAssignmentDetail>(`/api/homework/${assignmentId}`),
    enabled: Boolean(assignmentId),
    retry: false,
  });
}

export function useHomeworkTemplates(page = 1) {
  return useQuery({
    queryKey: queryKeys.homework.templates(page),
    queryFn: () => api.get<Paginated<HomeworkSummary>>('/api/homework/templates', { query: { page } }),
  });
}

function invalidateHomework(queryClient: ReturnType<typeof useQueryClient>, assignmentId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.homework.detail(assignmentId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.homework.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.student });
  void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.tutor });
}

export function useSaveHomeworkDraft(assignmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body?: string }) => api.patch(`/api/homework/${assignmentId}/draft`, input),
    onSuccess: () => invalidateHomework(queryClient, assignmentId),
  });
}

export function useSubmitHomework(assignmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body?: string }) =>
      api.post<HomeworkAssignmentDetail>(`/api/homework/${assignmentId}/submit`, input),
    onSuccess: () => invalidateHomework(queryClient, assignmentId),
  });
}

export function useMarkHomework(assignmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      score?: number;
      grade?: string;
      comment?: string;
      strengths?: string[];
      improvements?: string[];
    }) => api.post<HomeworkAssignmentDetail>(`/api/homework/${assignmentId}/mark`, input),
    onSuccess: () => invalidateHomework(queryClient, assignmentId),
  });
}

/* ------------------------------- progress ------------------------------- */

export function useMyProgress() {
  return useQuery({
    queryKey: queryKeys.progress.mine,
    queryFn: () => api.get<StudentProgressOverview>('/api/progress/me'),
  });
}

/* ------------------------------ resources ------------------------------- */

export function useResources(filters: { subjectId?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.resources.list(filters),
    queryFn: () => api.get<ResourceSummary[]>('/api/resources', { query: filters }),
  });
}

/* -------------------------------- profile ------------------------------- */

export interface ProfileResponse {
  user: StudentListItem['user'];
  tutorProfile: {
    id: string;
    headline: string | null;
    bio: string | null;
    qualifications: string[];
    yearsExperience: number | null;
    hourlyRate: number | null;
    currency: string;
    isVerified: boolean;
    subjects: Array<{ id: string; name: string; slug: string; colour: string | null }>;
  } | null;
  studentProfile: {
    id: string;
    educationLevel: string | null;
    yearGroup: string | null;
    school: string | null;
    targetGrade: string | null;
    guardianName: string | null;
    guardianEmail: string | null;
    guardianPhone: string | null;
    notes: string | null;
  } | null;
}

export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile.me,
    queryFn: () => api.get<ProfileResponse>('/api/me/profile'),
  });
}

/* ------------------------------ curriculum ------------------------------ */

export function useCurriculum() {
  return useQuery({
    queryKey: ['subjects'] as const,
    queryFn: () => api.get<CurriculumSubject[]>('/api/subjects'),
    // The curriculum barely changes; refetching it on every mount is wasted work.
    staleTime: 10 * 60_000,
  });
}

/* ----------------------------- invitations ------------------------------ */

export function useSentInvitations() {
  return useQuery({
    queryKey: ['invitations', 'sent'] as const,
    queryFn: () => api.get<SentInvitation[]>('/api/invitations/sent'),
  });
}

export function useReceivedInvitations() {
  return useQuery({
    queryKey: ['invitations', 'received'] as const,
    queryFn: () => api.get<ReceivedInvitation[]>('/api/invitations/received'),
  });
}

export function useInviteStudent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; subjectId?: string; message?: string }) =>
      api.post<SentInvitation>('/api/invitations', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<SentInvitation>(`/api/invitations/${id}/revoke`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useRespondToInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'decline' }) =>
      api.post<ReceivedInvitation>(`/api/invitations/${id}/${action}`),
    onSuccess: () => {
      // Accepting changes the roster, the dashboards and what can be scheduled.
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.student });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.tutor });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list });
    },
  });
}

/* -------------------------- lesson scheduling --------------------------- */

export interface CreateLessonPayload {
  title: string;
  description?: string;
  studentIds: string[];
  subjectId?: string;
  topicId?: string;
  objectives?: string[];
  scheduledStart: string;
  scheduledEnd: string;
}

function invalidateSchedule(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.tutor });
  void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.student });
}

export function useCreateLesson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLessonPayload) => api.post<LessonSummary>('/api/lessons', input),
    onSuccess: () => invalidateSchedule(queryClient),
  });
}

export function useUpdateLesson(lessonId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateLessonPayload>) =>
      api.patch<LessonSummary>(`/api/lessons/${lessonId}`, input),
    onSuccess: () => {
      invalidateSchedule(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.lessons.detail(lessonId) });
    },
  });
}

export function useCancelLesson(lessonId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      api.post<LessonSummary>(`/api/lessons/${lessonId}/cancel`, { reason }),
    onSuccess: () => {
      invalidateSchedule(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.lessons.detail(lessonId) });
    },
  });
}

/* -------------------------- homework authoring -------------------------- */

export function useCreateHomework() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      description?: string;
      instructions?: string;
      subjectId?: string;
      topicId?: string;
      maxScore?: number;
      estimatedMinutes?: number;
    }) => api.post<HomeworkSummary>('/api/homework/templates', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.homework.all }),
  });
}

export function useAssignHomework() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      homeworkId,
      ...input
    }: {
      homeworkId: string;
      studentIds: string[];
      dueAt: string;
      lessonId?: string;
    }) =>
      api.post<{ assigned: number; skipped: number; assignments: HomeworkAssignmentSummary[] }>(
        `/api/homework/templates/${homeworkId}/assign`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.homework.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.tutor });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.student });
    },
  });
}
